import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';

/** fteValue for hourly-paid assignments; the table shows a translated label. */
export const HOURLY = 'hourly';

const normalize = (value) => String(value).toLocaleLowerCase('tr');

/**
 * Projects for the employee's "Projeler" dialog: every project, each row with
 * the employee's assignments in it, plus the dialog's search and filters.
 */
const useEmployeeProjects = (employee) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showAssignedOnly, setShowAssignedOnly] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        const querySnapshot = await getDocs(collection(db, 'Projects'));
        setProjects(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error fetching projects:', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [employee]);

  // Çalışanın atama kayıtlarını proje bazında grupla (merkez/rol başına ayrı kayıt tutuluyor)
  const assignmentsByProject = useMemo(() => {
    const map = new Map();
    (employee.projects || []).forEach(assignment => {
      if (!assignment?.id) return;
      const list = map.get(assignment.id) || [];
      list.push(assignment);
      map.set(assignment.id, list);
    });
    return map;
  }, [employee.projects]);

  // Tablo satırları: proje + çalışanın o projedeki görev bilgileri
  const rows = useMemo(() => {
    const source = showAssignedOnly
      ? projects.filter(project => assignmentsByProject.has(project.id))
      : projects;

    return source.map(project => {
      const assignments = assignmentsByProject.get(project.id) || [];
      // Herhangi bir merkezde aktif görev varsa çalışan bu projede aktif sayılır
      const hasActive = assignments.some(a => a.status === 'active' || !a.status);
      const fteAssignment = assignments.find(a => a.rate?.type?.toUpperCase() === 'FTE');
      const rateType = (fteAssignment || assignments[0])?.rate?.type?.toUpperCase();

      let fteValue = '-';
      if (assignments.length > 0) {
        fteValue = rateType === 'FTE' ? (fteAssignment?.rate?.value ?? '-') : HOURLY;
      }

      return {
        project,
        assignments,
        assignmentStatus: assignments.length === 0 ? null : hasActive ? 'active' : 'passive',
        projectStatus: project.projectStatus || 'Aktif',
        fteValue,
      };
    });
  }, [projects, assignmentsByProject, showAssignedOnly]);

  const filteredRows = useMemo(() => {
    const term = normalize(searchTerm.trim());

    return rows.filter(row => {
      if (assignmentStatusFilter !== 'all' && row.assignmentStatus !== assignmentStatusFilter) return false;
      if (projectStatusFilter !== 'all' && row.projectStatus !== projectStatusFilter) return false;
      if (!term) return true;

      const { project, assignments } = row;
      const haystack = [
        project.titleShort,
        project.titleLong,
        project.protocolNumber,
        project.customer,
        project.sbProjectNumber,
        ...assignments.map(a => a.centerName),
        ...assignments.map(a => a.role),
      ];
      return haystack.some(value => value && normalize(value).includes(term));
    });
  }, [rows, searchTerm, assignmentStatusFilter, projectStatusFilter]);

  return {
    loading,
    loadFailed,
    rows,
    filteredRows,
    showAssignedOnly,
    setShowAssignedOnly,
    searchTerm,
    setSearchTerm,
    assignmentStatusFilter,
    setAssignmentStatusFilter,
    projectStatusFilter,
    setProjectStatusFilter,
  };
};

export default useEmployeeProjects;
